import {
  advancedFormControl,
  advancedFormArray,
  advancedFormGroup,
  FormControlTypeEnum,
} from '@magieno/common';

import { IsBoolean, IsInt, IsNotEmpty, IsOptional, validateIf, ValidateNested } from '@pristine-ts/class-validator';
import { translationBaseKey } from '../../../../../decorators/translation-base-key.decorator';
import { FieldTypeEnum } from '../../../../../enums/field-type.enum';
import { ErrorMessageBuilder } from '../../../../../builders/error-message.builder';
import { array } from '@pristine-ts/data-mapping-common';
import { ValidationGroupEnum } from '../../../../../enums/validation-group.enum';
import { IncomeTaxPersonalSpouseCommonOptions } from './income-tax-personal-spouse-common.options';
import { IncomeTaxPersonalChildCommonOptions } from './income-tax-personal-child-common.options';

@translationBaseKey('income-tax-personal-common-options')
export class IncomeTaxPersonalCommonOptions {
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que vous êtes mariés ou avez un conjoint de fait selon la LIR?',
        en: 'Are you married or do you have a common law partner ?',
      },
      tooltip: {
        fr: 'Indiquez si vous avez un partenaire. Le statut de mariage ou de partenariat peut avoir des conséquences fiscales différentes au Québec et au Canada.',
        en: 'Specify if you have a partner. The marital or partnership status may have different tax implications in both Quebec and Canada.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isMarried: boolean;

  @advancedFormGroup({
    classType: IncomeTaxPersonalSpouseCommonOptions,
  })
  @ValidateNested()
  mainPerson: IncomeTaxPersonalSpouseCommonOptions = new IncomeTaxPersonalSpouseCommonOptions();

  @advancedFormGroup({
    classType: IncomeTaxPersonalSpouseCommonOptions,
  })
  @validateIf((options: IncomeTaxPersonalCommonOptions) => {
    return options.isMarried === true;
  })
  @ValidateNested()
  @validateIf((options, value) => options.isMarried)
  spouse?: IncomeTaxPersonalSpouseCommonOptions;

  @array(IncomeTaxPersonalChildCommonOptions)
  @advancedFormArray({
    emptyNumberOfChildrenFormToCreate: 0,
    elementClassType: IncomeTaxPersonalChildCommonOptions,
  })
  @ValidateNested()
  children: IncomeTaxPersonalChildCommonOptions[] = [];

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année fiscale',
        en: 'Fiscal year',
      },
      tooltip: {
        fr: 'Indiquez l\'année fiscale pour laquelle vous effectuez le calcul. L\'année fiscale peut influencer les déductions et crédits d\'impôt applicables.',
        en: 'Specify the fiscal year for which you are performing the calculation. The fiscal year may impact the applicable tax deductions and credits.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional({ groups: [ValidationGroupEnum.SalaryDividend] })
  fiscalYear: number;

  // todo move this to federal only or add a condition in quebec
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Voulez-vous calculer les crédits et déductions pour frais de garde d’enfants?',
        en: 'Do you want to calculate childcare expenses?',
      },
      tooltip: {
        fr: 'Indiquez si les frais de garde doivent être calculés. Le Québec et le Canada peuvent offrir des avantages fiscaux ou des crédits pour les frais de garde admissibles engagés par les contribuables.',
        en: 'Specify if childcare expenses need to be calculated. Quebec and Canada may provide tax benefits or credits for eligible childcare expenses incurred by taxpayers.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  isCareExpensesCalculated: boolean;
}
